import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatOptionSelectionChange } from '@angular/material/core';
import { forkJoin } from 'rxjs';
import { saveAs } from 'file-saver';

import { AuthService } from '../../../services/auth.service';
import { AlertService, MessageSeverity } from '../../../services/alert.service';
import { ReportService } from '../../../services/report.service';
/*import { ProtocolService } from '../../../services/protocol.service';*/
import { Utilities } from '../../../services/utilities';

import { KappaForm } from '../../../models/report/kappa-form.model';
import { KappaType } from '../../../models/report/kappa-type.model';
import { FullProtocol } from '../../../models/protocol/full-protocol.model';
import { MechanismType } from '../../../models/kappa/mechanism-type.model';
import { QcWeekDay } from '../../../models/report/qc-week-day.model';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { KappaService } from '../../../services/kappa.service';
import { AccountService } from '../../../services/account.service';
import { ODPProjectWorkflowViewModel } from '../../../models/odpprojectworkflow/odpProjectWorkflow.model';


@Component({
  selector: 'app-report-kappa',
  templateUrl: './report-kappa.component.html',
  styleUrls: ['./report-kappa.component.css'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatFormFieldModule,
    MatRadioModule,
    MatSelectModule,
    ReactiveFormsModule,
    FormsModule
]
})
export class ReportKappaComponent implements OnInit {
  kappaForm: FormGroup;
  kappaOptions: KappaType[];
  kappaDefault: KappaType = new KappaType();
  dates: { text: string, value: string | QcWeekDay }[];
  weeks: QcWeekDay[];
  mechanisms: MechanismType[];
  lastKappaDate: string;
/*  protocols: FullProtocol[];*/
  years: string[];
  isFy = true;

  activeProject: ODPProjectWorkflowViewModel;
  activeProjectTitle: string;


  getFilename = (startDate: string, endDate: string, kappaType: string) =>
    `KappaAvgs ${kappaType} ${startDate}-${endDate} (${this.activeProjectTitle}) ${this.reportService.FormattedDateNoSeconds}.xlsx`

  constructor(
    private accountService: AccountService,
    private alertService: AlertService,
    private authService: AuthService,
    private kappaService: KappaService,
    private reportService: ReportService,
/*    private protocolService: ProtocolService,*/
    private formBuilder: FormBuilder,
    public router: Router,
  ) { }

  ngOnInit() {
    if (this.authService.isSessionExpired) {
      return this.router.navigateByUrl('/account/login');
    }

    this.getKappaTypes();
    this.initKappaForm();
    this.activeProject = this.accountService.GetActiveProject();
    this.activeProjectTitle = this.activeProject.projectTitle;
    this.loadData();
  }

  getKappaTypes(){
    this.kappaService.getKappaTypes().subscribe({
      next: (data) => {
        console.log(data);
        this.kappaOptions = data;
        this.kappaDefault = this.kappaOptions.find(k => k.isReportDefault == true);
        console.log(this.kappaDefault);
      }, error: (err) => {
        console.log(err)
      }
    });
  }

  initKappaForm() {
    this.kappaForm = this.formBuilder.group({
      isFy: true,
      kappaType: [, Validators.required],
      startDate: [, Validators.required],
      endDate: [, Validators.required],
      mechanisms: [[], Validators.required]
    }, { validators: [checkIfEndDateAfterStartDate] });
  }

  loadData() {
    this.alertService.startLoadingMessage();

    const getQCWeeks$ = this.reportService.getQcDates();
    const getFYs$ = this.reportService.getAbstractFYs(this.activeProject.odpProjectWorkflowId);
    const getMechanismTypes$ = this.reportService.getMechanismTypes(this.activeProject.odpProjectWorkflowId);
    const getLastKappaDate$ = this.reportService.getLastKappaReportDate(this.activeProject.odpProjectWorkflowId);
/*    const getProtocols$ = this.protocolService.getProtocols();*/

    //forkJoin([getQCWeeks$, getFYs$, getMechanismTypes$, getProtocols$]).subscribe({
    //  next: ([weeks, years, mechanisms, protocols]) => {
    //    this.alertService.stopLoadingMessage();
    //    this.weeks = weeks;
    //    this.years = years;
    //    this.mechanisms = mechanisms;
    //    this.protocols = protocols.reverse();

    //    this.dates = years.map(y => ({ text: y, value: y }));
    //    this.kappaForm.get('mechanisms').setValue([-1, ...this.mechanisms.map(m => m.id)]);
    //  },

    forkJoin([getQCWeeks$, getFYs$, getMechanismTypes$, getLastKappaDate$]).subscribe({
      next: ([weeks, years, mechanisms, lastKappaDate]) => {
        this.alertService.stopLoadingMessage();
        this.weeks = weeks;
        this.years = years;
        this.mechanisms = mechanisms;
        this.dates = years.map(y => ({ text: y, value: y}));
        this.kappaForm.get('mechanisms').setValue([-1, ...this.mechanisms.map(m => m.id)]);
        this.kappaForm.get('kappaType').setValue(this.kappaDefault);
        this.lastKappaDate = lastKappaDate;
      },
      error: (error) => {
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(
          'Load Error',
          `Unable to retrieve view data from the server.\r\nErrors: "${Utilities.getHttpResponseMessage(error)}"`,
          MessageSeverity.error,
          error);
    }});
  }

  toggleFY($event: MatRadioChange) {
    this.isFy = $event.value;
    this.dates = this.isFy ?
      this.years.map(y => ({ text: y, value: y})) :
      this.weeks.map(w => ({ text: `${w.qcWeek}: ${w.datesContractorCoded}`, value: w}));

    this.kappaForm.get('startDate').reset();
    this.kappaForm.get('endDate').reset();
  }

  toggleAllMechanisms($event: MatOptionSelectionChange) {
    if (!$event.isUserInput) { return; }
    this.kappaForm.get('mechanisms').setValue($event.source.selected ? this.mechanisms.map(m => m.id) : []);
  }

  uncheckSelectAllOnDeselect($event: MatOptionSelectionChange) {
    const mechanisms = this.kappaForm.get('mechanisms');
    if ($event.isUserInput && !$event.source.selected && mechanisms && mechanisms.value &&
        mechanisms.value[0] < 0 && mechanisms.value.length - 1 <= this.mechanisms.length) {
      mechanisms.setValue(mechanisms.value.filter((id: number) => id !== -1 && id !== $event.source.value));
    }
  }

  onSubmit(): void {
    var wait = 1;

    if (!this.kappaForm.valid) {
      this.alertService.showMessage(
        'Unable to submit.', 'Make sure all fields are filled and there are no errors shown.', MessageSeverity.error);
      return;
    }

    const kappaForm = {
      isFy: this.kappaForm.get('isFy').value,
      odpProjectWorkflowId: this.activeProject.odpProjectWorkflowId,
      kappaType: this.kappaForm.get('kappaType').value.id,
      startDate: this.isFy
        ? this.kappaForm.get('startDate').value
        : (this.kappaForm.get('startDate').value as QcWeekDay).datesContractorCoded,
      endDate: this.isFy
        ? this.kappaForm.get('endDate').value
        : (this.kappaForm.get('endDate').value as QcWeekDay).datesContractorCoded,
      mechanismIds: this.kappaForm.get('mechanisms').value.filter((x: number) => x !== -1),
      kappaAbbrev: this.kappaForm.get('kappaType').value.kappaType1,
      kappaDesc: this.kappaForm.get('kappaType').value.description
    } as KappaForm;

    this.alertService.startLoadingMessage();
    this.reportService.getKappaReport(kappaForm).subscribe({
      next: (data: ArrayBuffer) => {
        this.alertService.stopLoadingMessage();

        if (data) {
          const startDate = kappaForm.isFy ? kappaForm.startDate : kappaForm.startDate.replace(/\//g, '');
          const endDate = kappaForm.isFy ? kappaForm.endDate : kappaForm.endDate.replace(/\//g, '');

          const blob = new Blob([data], {type: this.reportService.ReportContentType});
          saveAs(blob, this.getFilename(startDate, endDate, kappaForm.kappaAbbrev));
        } else {
          this.alertService.showStickyMessage(
            'Empty Result',
            `Kappa report returned an empty result for dates ${kappaForm.startDate} - ${kappaForm.endDate}.`,
            MessageSeverity.warn);
        }
      },
      error: (error) => {
        this.alertService.stopLoadingMessage();
        this.alertService.showStickyMessage(
          'Server error',
          `An error ocurred while building the kappa report.\r\nErrors: "${Utilities.getHttpResponseMessage(error)}"`,
          MessageSeverity.error,
          error);
      }});
  }
}

export function checkIfEndDateAfterStartDate(control: AbstractControl) {
  const startDateControl = control.get('startDate');
  const endDateControl = control.get('endDate');
  const isFyControl = control.get('isFy');

  if (!startDateControl.value || !endDateControl.value) { return control.errors; }

  const startDateValue = isFyControl.value ? startDateControl.value : startDateControl.value['qcWeek'];
  const endDateValue = isFyControl.value ? endDateControl.value : endDateControl.value['qcWeek'];

  // Return invalid date error.
  if (startDateValue > endDateValue) {
    startDateControl.setErrors({...startDateControl.errors, ...{invalidEndDate: true}});
    endDateControl.setErrors({...endDateControl.errors, ...{invalidEndDate: true}});
    return {...control.errors, ...{invalidEndDate: true}};
  }

  // Remove invalid date errors if they exist.
  if (startDateControl.errors) {
    if (startDateControl.errors.invalidEndDate) { delete startDateControl.errors.invalidEndDate; }
    if (!Object.keys(startDateControl.errors).length) { startDateControl.setErrors(null); }
  }

  if (endDateControl.errors) {
    if (endDateControl.errors.invalidEndDate) { delete endDateControl.errors.invalidEndDate; }
    if (!Object.keys(endDateControl.errors).length) { endDateControl.setErrors(null); }
  }

  if (control.errors) {
    if (control.errors.invalidEndDate) { delete control.errors.invalidEndDate; }
    if (!Object.keys(control.errors).length) { return null; }
  }

  return control.errors;
}
